import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, BookOpenCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/6e0fc21a-cb80-4712-b8a2-ad8db5b9866b/images/07cdcddce393b83511848cc8fe9a75a89a1af59cc01f06087a16701966566444.png";
const BG = "https://images.pexels.com/photos/7130496/pexels-photo-7130496.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Login = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@bukukas.id");
  const [password, setPassword] = useState("admin123");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, remember);
      toast.success("Selamat datang kembali!");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal masuk");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Form side */}
      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-1/2 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3">
            <img src={LOGO} alt="BukuKas" className="h-11 w-11 rounded-xl object-cover" />
            <div>
              <h1 className="font-heading text-xl font-extrabold tracking-tight">BukuKas Pro</h1>
              <p className="text-xs text-muted-foreground">Pembukuan Usaha Modern</p>
            </div>
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight">Masuk ke akun Anda</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Kelola invoice, penawaran, dan keuangan usaha Anda.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@usaha.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  data-testid="toggle-password-btn"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={remember}
                  onCheckedChange={(v) => setRemember(!!v)}
                  data-testid="remember-checkbox"
                />
                Ingat saya
              </label>
            </div>

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BookOpenCheck className="mr-2 h-4 w-4" />}
              Masuk
            </Button>
          </form>

          <p className="mt-6 rounded-lg bg-secondary p-3 text-center text-xs text-muted-foreground">
            Demo: <span className="font-medium text-foreground">admin@bukukas.id</span> / <span className="font-medium text-foreground">admin123</span>
          </p>
        </div>
      </div>

      {/* Image side */}
      <div className="relative hidden lg:block lg:w-1/2">
        <img src={BG} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-700/80 via-blue-600/40 to-cyan-500/30" />
        <div className="absolute bottom-16 left-12 right-12 text-white">
          <h3 className="font-heading text-3xl font-bold leading-tight">
            Pembukuan usaha jadi lebih mudah & profesional.
          </h3>
          <p className="mt-3 max-w-md text-white/80">
            Invoice otomatis, laporan laba rugi, manajemen customer, hingga penawaran — semua dalam satu dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
